import React, { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, Pause, Play, Square } from "lucide-react";

/**
 * TextToSpeech — reads the provided text aloud using the browser's
 * Web Speech API (SpeechSynthesis). No backend required.
 *
 * Props:
 *  - text: string to read
 *  - label: optional button label (default "Listen")
 *  - compact: smaller styling variant
 */
export default function TextToSpeech({ text, label = "Listen", compact = false }) {
  const [supported, setSupported] = useState(true);
  const [status, setStatus] = useState("idle"); // idle | playing | paused
  const utterRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      setSupported(false);
    }
    return () => {
      // stop any speech when component unmounts
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return null;
    // Prefer an Indian English voice, then any English voice
    return (
      voices.find((v) => /en-IN/i.test(v.lang)) ||
      voices.find((v) => /en[-_]?GB/i.test(v.lang)) ||
      voices.find((v) => /^en/i.test(v.lang)) ||
      voices[0]
    );
  };

  const start = useCallback(() => {
    const synth = window.speechSynthesis;
    synth.cancel();

    const utter = new SpeechSynthesisUtterance(text);
    const voice = pickVoice();
    if (voice) utter.voice = voice;
    utter.rate = 0.96;
    utter.pitch = 1;
    utter.onend = () => setStatus("idle");
    utter.onerror = () => setStatus("idle");
    utterRef.current = utter;
    synth.speak(utter);
    setStatus("playing");
  }, [text]);

  const handleMain = () => {
    const synth = window.speechSynthesis;
    if (status === "idle") {
      start();
    } else if (status === "playing") {
      synth.pause();
      setStatus("paused");
    } else if (status === "paused") {
      synth.resume();
      setStatus("playing");
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setStatus("idle");
  };

  if (!supported) return null;

  const MainIcon =
    status === "playing" ? Pause : status === "paused" ? Play : Volume2;
  const mainText =
    status === "playing" ? "Pause" : status === "paused" ? "Resume" : label;

  const base = compact ? "text-sm px-4 py-2" : "px-5 py-2.5";

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={handleMain}
        className={`inline-flex items-center gap-2 rounded-md font-medium transition-colors duration-300 ${base} ${
          status === "idle"
            ? "bg-[#1a2b4a] text-white hover:bg-[#243a63]"
            : "bg-[#b8935a] text-white hover:bg-[#a67f45]"
        }`}
      >
        <MainIcon size={17} />
        {mainText}
      </button>
      {status !== "idle" && (
        <button
          onClick={handleStop}
          className="inline-flex items-center gap-1.5 rounded-md border border-[#d8d2c4] text-[#1a2b4a] hover:border-[#b8935a] px-3 py-2.5 text-sm font-medium transition-colors duration-300"
          aria-label="Stop"
        >
          <Square size={15} /> Stop
        </button>
      )}
    </div>
  );
}
